import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Plus, Edit2, Trash2, X, Save } from 'lucide-react';
import axios from 'axios';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { toast } from '../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminProjects = () => {
  const { getAuthHeader } = useAdminAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    status: 'Planning',
    category: 'Infrastructure',
    description: '',
    start_date: '',
    expected_completion: '',
    completion: 0,
    budget: ''
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/projects`);
      setProjects(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load projects', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API_URL}/api/projects/${editingId}`, formData, { headers: getAuthHeader() });
        toast({ title: 'Success', description: 'Project updated successfully' });
      } else {
        await axios.post(`${API_URL}/api/projects`, formData, { headers: getAuthHeader() });
        toast({ title: 'Success', description: 'Project created successfully' });
      }
      resetForm();
      fetchProjects();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save project', variant: 'destructive' });
    }
  };

  const handleEdit = (item) => {
    setFormData({
      title: item.title,
      status: item.status,
      category: item.category,
      description: item.description,
      start_date: item.start_date,
      expected_completion: item.expected_completion,
      completion: item.completion,
      budget: item.budget
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await axios.delete(`${API_URL}/api/projects/${id}`, { headers: getAuthHeader() });
      toast({ title: 'Success', description: 'Project deleted' });
      fetchProjects();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({ title: '', status: 'Planning', category: 'Infrastructure', description: '', start_date: '', expected_completion: '', completion: 0, budget: '' });
    setEditingId(null);
    setShowForm(false);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">Projects Management</h1>
          <button onClick={() => setShowForm(true)} className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center space-x-2">
            <Plus className="w-5 h-5" /><span>Add Project</span>
          </button>
        </div>

        {showForm && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">{editingId ? 'Edit' : 'Add'} Project</h2>
              <button onClick={resetForm}><X className="w-6 h-6" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Title</label>
                <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required className="w-full px-4 py-2 border rounded-lg" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Status</label>
                  <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} className="w-full px-4 py-2 border rounded-lg">
                    <option>Planning</option>
                    <option>In Progress</option>
                    <option>Completed</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Category</label>
                  <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} className="w-full px-4 py-2 border rounded-lg">
                    <option>Infrastructure</option>
                    <option>Health</option>
                    <option>Education</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows="3" required className="w-full px-4 py-2 border rounded-lg" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Start Date</label>
                  <input type="text" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} placeholder="August 2023" required className="w-full px-4 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Expected Completion</label>
                  <input type="text" value={formData.expected_completion} onChange={(e) => setFormData({ ...formData, expected_completion: e.target.value })} placeholder="December 2024" required className="w-full px-4 py-2 border rounded-lg" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Budget</label>
                  <input type="text" value={formData.budget} onChange={(e) => setFormData({ ...formData, budget: e.target.value })} placeholder="K2.5M" required className="w-full px-4 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Completion %</label>
                  <input type="number" min="0" max="100" value={formData.completion} onChange={(e) => setFormData({ ...formData, completion: parseInt(e.target.value) })} required className="w-full px-4 py-2 border rounded-lg" />
                </div>
              </div>
              <button type="submit" className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg">
                <Save className="w-5 h-5 inline mr-2" />{editingId ? 'Update' : 'Create'}
              </button>
            </form>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {loading ? <div className="p-8 text-center">Loading...</div> : projects.length === 0 ? <div className="p-8 text-center">No projects yet</div> : (
            <div className="divide-y">
              {projects.map((item) => (
                <div key={item.id} className="p-6 hover:bg-gray-50">
                  <div className="flex justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                      <p className="text-gray-600 mb-2">{item.description}</p>
                      <div className="flex items-center space-x-4 text-sm">
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full">{item.status}</span>
                        <span className="text-gray-500">{item.category}</span>
                        <span className="text-gray-500">{item.completion}%</span>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button onClick={() => handleEdit(item)} className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg"><Edit2 className="w-5 h-5" /></button>
                      <button onClick={() => handleDelete(item.id)} className="p-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg"><Trash2 className="w-5 h-5" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminProjects;